import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import TenantService, { BusinessProfile, UpdateBusinessProfilePayload } from '../services/TenantService';
import { personalakteStyles as ps, colors } from '../../../employees/styles/personalakteStyles';

interface CompanyProfileTabProps {
  profile: BusinessProfile | null;
  loading: boolean;
  onProfileUpdated: () => void;
}

interface FormField {
  key: keyof UpdateBusinessProfilePayload;
  label: string;
  type: 'text' | 'email' | 'number';
  placeholder?: string;
  readOnly?: boolean;
  half?: boolean;
}

interface FormSection {
  title: string;
  fields: FormField[];
  helperText?: string;
}

const formSections: FormSection[] = [
  {
    title: 'Company',
    fields: [
      { key: 'legal_name', label: 'Legal company name', type: 'text', placeholder: 'e.g. My Company GmbH' },
      { key: 'trade_name', label: 'Brand / trading name', type: 'text', placeholder: 'e.g. My Restaurant' },
    ],
  },
  {
    title: 'Address',
    fields: [
      { key: 'street', label: 'Street', type: 'text', placeholder: 'Main Street', half: true },
      { key: 'street_number', label: 'House number', type: 'text', placeholder: '123', half: true },
      { key: 'postal_code', label: 'Postal code', type: 'text', placeholder: '12345', half: true },
      { key: 'city', label: 'City', type: 'text', placeholder: 'Berlin', half: true },
      { key: 'country', label: 'Country', type: 'text', placeholder: 'Germany' },
    ],
  },
  {
    title: 'Tax information',
    fields: [
      { key: 'tax_number', label: 'Tax number', type: 'text', placeholder: '123/456/78901', half: true },
      { key: 'vat_id', label: 'VAT ID', type: 'text', placeholder: 'DE123456789', half: true },
      { key: 'commercial_register_number', label: 'Commercial register no.', type: 'text', placeholder: 'HRB 12345', half: true },
      { key: 'commercial_register_court', label: 'Commercial register court', type: 'text', placeholder: 'Amtsgericht Berlin', half: true },
    ],
  },
  {
    title: 'Vertretung',
    helperText: 'Zeichnungsberechtigte Person (erscheint auf Verträgen und Angeboten als "vertreten durch").',
    fields: [
      { key: 'signatory_name', label: 'Name (Geschäftsführer:in)', type: 'text', placeholder: 'Conny Köhler', half: true },
      { key: 'signatory_title', label: 'Titel', type: 'text', placeholder: 'Geschäftsführerin', half: true },
    ],
  },
  {
    title: 'Bank details',
    helperText: 'These bank details will appear on offers and invoices.',
    fields: [
      { key: 'bank_name', label: 'Bank name', type: 'text', placeholder: 'Deutsche Bank' },
      { key: 'iban', label: 'IBAN', type: 'text', placeholder: 'DE89 3704 0044 0532 0130 00', half: true },
      { key: 'bic', label: 'BIC', type: 'text', placeholder: 'COBADEFFXXX', half: true },
    ],
  },
  {
    title: 'Communication',
    helperText: 'Configure how emails for offers and invoices are sent.',
    fields: [
      { key: 'default_sender_email', label: 'Default sender email', type: 'email', placeholder: 'invoice@company.com', half: true },
      { key: 'reply_to_email', label: 'Reply-to email', type: 'email', placeholder: 'office@company.com', half: true },
      { key: 'default_payment_terms_days', label: 'Default payment terms (days)', type: 'number', placeholder: '14' },
      { key: 'phone', label: 'Phone', type: 'text', placeholder: '+49 30 12345678', half: true },
      { key: 'website', label: 'Website', type: 'text', placeholder: 'https://www.company.com', half: true },
    ],
  },
];

export default function CompanyProfileTab({
  profile,
  loading,
  onProfileUpdated,
}: CompanyProfileTabProps): React.JSX.Element {
  const [formData, setFormData] = useState<UpdateBusinessProfilePayload>({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        legal_name: profile.legal_name || '',
        trade_name: profile.trade_name || '',
        street: profile.street || '',
        street_number: profile.street_number || '',
        postal_code: profile.postal_code || '',
        city: profile.city || '',
        country: profile.country || '',
        tax_number: profile.tax_number || '',
        vat_id: profile.vat_id || '',
        commercial_register_number: profile.commercial_register_number || '',
        commercial_register_court: profile.commercial_register_court || '',
        bank_name: profile.bank_name || '',
        iban: profile.iban || '',
        bic: profile.bic || '',
        default_payment_terms_days: profile.default_payment_terms_days,
        default_sender_email: profile.default_sender_email || '',
        reply_to_email: profile.reply_to_email || '',
        phone: profile.phone || '',
        website: profile.website || '',
        signatory_name: profile.signatory_name || '',
        signatory_title: profile.signatory_title || '',
      });
      setHasChanges(false);
    }
  }, [profile]);

  const handleChange = (key: keyof UpdateBusinessProfilePayload, value: string | number) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await TenantService.updateBusinessProfile(formData);
      toast.success('Company profile saved');
      setHasChanges(false);
      onProfileUpdated();
    } catch (err: any) {
      console.error('Failed to save business profile:', err);
      const message = err?.response?.data?.detail || err?.response?.data?.message || 'Save failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        legal_name: profile.legal_name || '',
        trade_name: profile.trade_name || '',
        street: profile.street || '',
        street_number: profile.street_number || '',
        postal_code: profile.postal_code || '',
        city: profile.city || '',
        country: profile.country || '',
        tax_number: profile.tax_number || '',
        vat_id: profile.vat_id || '',
        commercial_register_number: profile.commercial_register_number || '',
        commercial_register_court: profile.commercial_register_court || '',
        bank_name: profile.bank_name || '',
        iban: profile.iban || '',
        bic: profile.bic || '',
        default_payment_terms_days: profile.default_payment_terms_days,
        default_sender_email: profile.default_sender_email || '',
        reply_to_email: profile.reply_to_email || '',
        phone: profile.phone || '',
        website: profile.website || '',
        signatory_name: profile.signatory_name || '',
        signatory_title: profile.signatory_title || '',
      });
      setHasChanges(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Loading...</div>;
  }

  return (
    <div>
      {formSections.map((section) => (
        <div key={section.title} style={{ marginBottom: 28 }}>
          <div style={ps.sectionHeader}>
            <div style={ps.sectionDot(colors.magenta)} />
            {section.title}
          </div>
          {section.helperText && (
            <p style={{ margin: '0 0 14px 0', fontSize: 12, color: colors.textMuted }}>{section.helperText}</p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {section.fields.map((field) => (
              <div
                key={field.key}
                style={{
                  gridColumn: field.half ? 'auto' : '1 / -1',
                }}
              >
                <div style={ps.fieldWrapper}>
                  <label style={ps.labelStyle}>{field.label}</label>
                  <input
                    type={field.type}
                    value={(formData[field.key] as string | number) ?? ''}
                    onChange={(e) =>
                      handleChange(field.key, field.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)
                    }
                    placeholder={field.placeholder}
                    readOnly={field.readOnly}
                    style={{
                      ...ps.inputStyle,
                      ...(field.readOnly ? { color: colors.textLight, cursor: 'not-allowed' } : {}),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 20, borderTop: `1px solid ${colors.borderLight}` }}>
        <button
          onClick={handleCancel}
          disabled={!hasChanges || saving}
          className="hover-btn-secondary"
          style={{
            ...ps.buttonSecondary,
            opacity: hasChanges && !saving ? 1 : 0.4,
            cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="hover-btn-primary"
          style={{
            ...ps.buttonPrimary,
            opacity: hasChanges && !saving ? 1 : 0.4,
            cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
